import React, { useEffect, useRef, useState } from 'react';
import { useFetch } from 'hooks/use-fetch';
import AutosuggestBasic from 'layouts/header/search/components/autosuggest-basic';
import SuggestionView from 'layouts/header/search/components/suggestion-view';
import Icon from 'atomic/icon';

import './autosuggest.scss';

const SUGGESTIONS_DEBOUNCE_TIME = 200;

interface SuggestionsFetchRequestedInterface {
    highlighted: string;
    phrase: string;
    url: string;
}

const Autosuggest: React.FC<{
    onSubmit: () => void;
    onChange: any;
    onFocus?: () => void;
    onBlur?: () => void;
    searchValue: string;
    categoryId: string;
}> = ({ onSubmit, searchValue, categoryId, onChange, onFocus, onBlur }) => {
    const [AutosuggestComponent, setAutosuggestComponent] = useState<React.ComponentType<any>>(() => AutosuggestBasic);
    const { get } = useFetch('/search/suggestions', { method: 'GET' });

    const [suggestions, setSuggestions] = useState<SuggestionsFetchRequestedInterface[]>([]);
    const suggestionsTimer = useRef<ReturnType<typeof setTimeout>>();
    // Bumped on every discard. `useFetch` takes no abort signal, so a request that already left
    // cannot be stopped — its response is matched against this id instead and dropped when stale.
    const requestId = useRef(0);

    // Drops both a scheduled fetch and the response of one already in flight.
    const discardPendingSuggestions = (): void => {
        clearTimeout(suggestionsTimer.current);
        requestId.current += 1;
    };

    useEffect(() => {
        import('react-autosuggest').then(mod => {
            setAutosuggestComponent(() => mod.default);
        });
    }, []);

    useEffect(() => discardPendingSuggestions, []);

    // react-autosuggest calls this on every keystroke, so without a delay typing one word sent one
    // request per character — each a fuzzy suggest query on the products index.
    const onSuggestionsFetchRequested = ({ value }: any): void => {
        discardPendingSuggestions();

        if (value.length < 3) {
            return;
        }

        suggestionsTimer.current = setTimeout(() => {
            const currentRequestId = requestId.current;

            get({ query: value, categoryId: categoryId !== '' ? categoryId : '*' })
                .then((response: SuggestionsFetchRequestedInterface[]) => {
                    if (response && currentRequestId === requestId.current) {
                        setSuggestions(response);
                    }
                });
        }, SUGGESTIONS_DEBOUNCE_TIME);
    };

    const onSuggestionsClearRequested = (): void => {
        discardPendingSuggestions();
        setSuggestions([]);
    };

    const getSuggestionValue = (suggestion: any): string => suggestion.highlighted ? suggestion.phrase : '';

    const inputProps = {
        name: 'query',
        placeholder: 'Znajdź produkt...',
        value: searchValue,
        onChange,
        onFocus,
        onBlur
    };

    let clearButton;

    if (searchValue && searchValue.length >= 1) {
        clearButton = (
            <button
                type="button"
                className="react-autosuggest__clear-button"
                onClick={(): void => onChange('', { newValue: '' })}
            >
                <Icon name="x-circle" size={18} />
            </button>
        );
    }

    return (
        <div className="react-autosuggest">
            <AutosuggestComponent
                suggestions={suggestions}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={(suggestion: any): any => <SuggestionView suggestion={suggestion} />}
                inputProps={inputProps}
                onSuggestionSelected={onSubmit}
            />
            {clearButton}
        </div>
    );
};

export default Autosuggest;
